import React, { useState } from "react";
import { motion } from "framer-motion";
import InputLabel from "./InputLabel";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import Image from "../images/Image";
import { getMaxLength } from "../commonfunctions/getMaxLength";
import useTranslation from "../CustomHooks/useTranslation";
import { inputFieldContent } from "./InputFieldContent";

interface InputItem {
  labelName: string;
  id: string;
}

interface InputFieldProps {
  inputItem: InputItem;
  errorMessage?: string;
  fieldError?: string;
  testId?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  isUpdatePassword?: boolean;
  maxLength?: number;
}

const PasswordField: React.FC<InputFieldProps> = ({ inputItem, errorMessage, onChange, fieldError, testId, required, isUpdatePassword, maxLength }) => {
  const { labelName, id } = inputItem;
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  const { translate, language } = useTranslation(inputFieldContent)

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prevState) => !prevState);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const maxLength = getMaxLength(id);
    let { value } = event.target;

    // Remove spaces from the password input
    value = value.replace(/\s/g, ''); // This removes all spaces

    // Enforce maxLength restriction
    if (maxLength !== undefined && value.length > maxLength) {
      value = value.slice(0, maxLength);
    }

    setPasswordValue(value);
    event.target.value = value; // Update the value in the input field
    onChange(event); // Trigger the parent onChange handler
  };

  const inputVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
  };

  const labelVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
  };



  return (
    <div className="w-full">
      {/* Label animation */}
      <motion.div
        variants={labelVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <InputLabel labelName={labelName} id={id} required={required} />
      </motion.div>

      {/* Input field animation */}
      <motion.div
        variants={inputVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.4, ease: "easeInOut" }}
        className="relative"
      >
        <input
          id={id}
          data-testid={testId || id}
          type={isPasswordVisible ? "text" : "password"}
          className={`w-full h-14 py-5 pr-[15px] pl-2.5 border rounded-lg ${isPasswordVisible ? 'text-subTitle' : 'placeholder:font-medium font-bold'} outline-none focus:border-2 ${(errorMessage || fieldError) ? "border-negative" : "border-P_light_active"
            } custom-password`}
          placeholder={translate(`Enter ${labelName.toLocaleLowerCase()}`)}
          value={passwordValue}
          onChange={handleInputChange}
          maxLength={maxLength}
        />

        <motion.button
          type="button"
          className={`absolute ${language === 'english' ? 'right-0' : 'left-4'} top-4 transform -translate-y-1/2 mr-3`}
          onClick={togglePasswordVisibility}
          variants={buttonVariants}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.4, ease: "easeInOut" }}
        >
          <Image src={isPasswordVisible ? "hidden" : "show"} />
        </motion.button>

      </motion.div>

      {
        errorMessage && (
          <ErrorMessage errorMessage={errorMessage} />
        )
      }
    </div >
  );
};

export default PasswordField;
